import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import type { IPublicClientApplication } from "@azure/msal-browser";

interface ResourceState<T> {
  key: string;
  data: T | null;
  error: string | null;
}

/**
 * Loads one resource by key with abort-on-change semantics. The result is atomic and keyed, so a
 * key change shows "loading" again without an intermediate stale render. `setData` lets actions
 * (regenerate, retry) replace the loaded value in place.
 */
export function useResource<T>(
  key: string,
  load: (msal: IPublicClientApplication, key: string, signal: AbortSignal) => Promise<T>,
) {
  const { instance } = useMsal();
  const [state, setState] = useState<ResourceState<T>>({ key: "", data: null, error: null });

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    load(instance, key, controller.signal)
      .then((data) => {
        if (!ignore) setState({ key, data, error: null });
      })
      .catch((err: Error) => {
        if (!ignore && err.name !== "AbortError") setState({ key, data: null, error: err.message });
      });

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [instance, key, load]);

  const loading = state.key !== key;
  const setData = (data: T) => setState({ key, data, error: null });

  return { data: loading ? null : state.data, error: loading ? null : state.error, loading, setData };
}
